import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import { DataTypeLocator } from '@helo/cms'
import { useForm } from 'react-hook-form'

import { useAppStore } from '../../store/appStore/appStore'
import {
  ColumnState,
  TableColumnConfig,
} from '../../widgets/TableWidget/tablewidget.parts'
import { LightModeModalContent } from '../Modal/LightModeModalContent'
import { showToast } from '../Toast/showToast'
import { TextAreaInput } from '../inputs/TextAreaInput'
import { TextInput } from '../inputs/TextInput'

type SaveViewModalFormData = {
  name: string
  description?: string
}

const NAME_MAX_LENGTH = 50
const DESCRIPTION_MAX_LENGTH = 150

export interface SaveViewModalProps extends ModalProps {
  initialState?: SaveViewModalFormData
  columns?: ColumnState[]
  columnConfigs?: TableColumnConfig[]
  connectionLocator?: DataTypeLocator
  queryId?: string
  widgetId?: string
}

interface SaveViewModalContentProps {
  initialState?: SaveViewModalFormData
  columns?: ColumnState[]
  columnConfigs?: TableColumnConfig[]
  connectionLocator?: DataTypeLocator
  queryId?: string
  widgetId?: string
  onClose?: ModalProps['onClose']
}

const SaveViewModalContent = ({
  initialState,
  columns,
  columnConfigs,
  connectionLocator,
  queryId,
  widgetId,
  onClose,
}: SaveViewModalContentProps) => {
  const { viewId, savedView, createSavedView, updateSavedView } = useAppStore(
    ({ viewStore }) => ({
      viewId: viewStore.viewId,
      savedView: viewStore.savedView,
      createSavedView: viewStore.createSavedView,
      updateSavedView: viewStore.updateSavedView,
    }),
  )

  const currentUser = useAppStore(state => state.auth.user)
  // Determine if we have a currently loaded saved view
  const isEditingExistingView = Boolean(savedView)

  // Check if current user owns the saved view
  const currentUserId = currentUser?.id
  const isOwner =
    savedView?.userId && currentUserId
      ? savedView.userId === currentUserId
      : true // Default to true if we can't determine (for new views)

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, errors },
  } = useForm<SaveViewModalFormData>({
    defaultValues: {
      description: initialState?.description ?? savedView?.description ?? '',
      name: initialState?.name ?? savedView?.name ?? '',
    },
    mode: 'onChange',
  })

  // Watch name field to see if it changed
  const currentName = watch('name')
  const isNameChanged = isEditingExistingView && currentName !== savedView?.name

  // Watch fields for character counting
  const nameValue = currentName || ''
  const nameCharCount = nameValue.length
  const descriptionValue = watch('description') || ''
  const descriptionCharCount = descriptionValue.length

  const saveView = async (data: SaveViewModalFormData) => {
    if (!viewId) {
      // TODO: handle this case somehow.
      throw new Error('View ID is required')
    }

    try {
      // If user doesn't own the saved view, always create a new one
      const shouldCreateNew = !isOwner || isNameChanged
      const isUpdatingExistingView =
        isEditingExistingView && !isNameChanged && savedView?.id && isOwner

      const tableConfig = {
        columns,
        columnConfigs,
        connectionLocator,
        queryId,
        widgetId,
      }

      if (isUpdatingExistingView) {
        // Update existing saved view (name hasn't changed and user owns it)
        await updateSavedView({
          name: data.name,
          description: data.description ?? '',
          tableConfig,
        })

        showToast({
          id: 'updated-view-toast',
          title: 'View Updated',
          description: `"${data.name}" was successfully updated!`,
        })
      } else {
        // Create a new saved view (either new view, name changed, or user doesn't own it)
        await createSavedView({
          name: data.name,
          description: data.description ?? '',
          tableConfig,
        })

        showToast({
          id: 'saved-view-toast',
          title: 'View Saved',
          description: `"${data.name}" was successfully saved!`,
        })
      }

      onClose?.()
    } catch (error) {
      console.error('Error saving view:', error)
      const action =
        isEditingExistingView && !isNameChanged && isOwner ? 'update' : 'save'
      showToast({
        id: 'saved-view-error-toast',
        title: 'Error',
        description: `Failed to ${action} view.`,
        variant: 'error',
      })
    }
  }

  // Determine modal title and button text based on context
  const getModalTitle = () => {
    if (isEditingExistingView) {
      if (!isOwner) {
        return 'Save as New View'
      }
      return isNameChanged ? 'Save as New View' : 'Update View'
    }
    return 'Save As New View'
  }

  const getButtonText = () => {
    if (isEditingExistingView) {
      if (!isOwner) {
        return 'Save as New View'
      }
      return isNameChanged ? 'Save' : 'Update Saved View'
    }
    return 'Save'
  }

  return (
    <>
      <ModalOverlay />
      <LightModeModalContent>
        <form onSubmit={handleSubmit(saveView)}>
          <ModalHeader>{getModalTitle()}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt="35px" pb="50px">
            <VStack gap="24px">
              <FormControl isInvalid={!!errors.name}>
                <FormLabel>View Name</FormLabel>
                <TextInput
                  {...register('name', {
                    required: 'View name is required',
                    validate: value =>
                      value.trim() !== '' || 'View name cannot be empty',
                    maxLength: {
                      value: NAME_MAX_LENGTH,
                      message: `View name cannot exceed ${NAME_MAX_LENGTH} characters`,
                    },
                  })}
                  autoComplete="off"
                  placeholder="My Saved View..."
                  autoFocus={true}
                  maxLength={NAME_MAX_LENGTH}
                />
                <FormErrorMessage fontSize="xs" mt="0">
                  {errors.name?.message}
                </FormErrorMessage>
                <Text fontSize="xs" textAlign="right">
                  {nameCharCount}/{NAME_MAX_LENGTH}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>Description (optional)</FormLabel>
                <TextAreaInput
                  {...register('description', {
                    maxLength: {
                      value: DESCRIPTION_MAX_LENGTH,
                      message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`,
                    },
                  })}
                  placeholder="Description..."
                  maxLength={DESCRIPTION_MAX_LENGTH}
                />
                <Text fontSize="xs" textAlign="right">
                  {descriptionCharCount}/{DESCRIPTION_MAX_LENGTH}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                onClose?.()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              px="40px"
              isDisabled={isSubmitting || !isValid}
            >
              {getButtonText()}
            </Button>
          </ModalFooter>
        </form>
      </LightModeModalContent>
    </>
  )
}

export const SaveViewModal = ({
  initialState,
  columns,
  columnConfigs,
  connectionLocator,
  queryId,
  widgetId,
  ...props
}: SaveViewModalProps) => {
  return (
    <Modal size="xl" {...props}>
      {props.isOpen && (
        <SaveViewModalContent
          initialState={initialState}
          columns={columns}
          columnConfigs={columnConfigs}
          connectionLocator={connectionLocator}
          queryId={queryId}
          widgetId={widgetId}
          onClose={props.onClose}
        />
      )}
    </Modal>
  )
}
